import { cn } from '@app/utils/cn';
import { useDebouncedCallback } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Key } from '@app/utils/keys';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-laag';

import { Search as SearchIcon } from '../Icons';

import { ClearButton } from './ClearButton';

interface Props {
  onChange: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  placeholder?: string;
  size?: 'large' | 'regular';
  className?: string;
  isFolded?: boolean;
  isFluid?: boolean;
  value?: string;
  isDisabled?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

export const Search = React.forwardRef<HTMLInputElement, React.WithOptionalChildren<Props>>((props, ref) => {
  const {
    onChange,
    onBlur,
    onFocus,
    placeholder = '',
    size = 'large',
    className,
    isFolded = false,
    isFluid = false,
    isDisabled = false,
    value = '',
    children,
    onKeyDown,
  } = props;
  const { t } = useTranslation(['common']);
  const internalRef = React.useRef<HTMLInputElement | null>(null);

  const [searchValue, setSearchValue] = React.useState(value);
  const [prevValue, setPrevValue] = React.useState(value);

  if (prevValue !== value) {
    setSearchValue(value);
    setPrevValue(value);
  }

  const debouncedOnChange = useDebouncedCallback(onChange);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    setSearchValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setSearchValue('');
    onChange('');
    internalRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    onKeyDown?.(event);

    if (event.code === Key.ESCAPE && hasValue) {
      event.stopPropagation();

      handleClear();
    }
  };

  const hasValue = !isEmpty(searchValue.toString());

  const hasValidChildren = isDefined(children) && children !== false;

  return (
    <div
      role="search"
      className={cn(
        `relative flex h-8 w-50 shrink-0 items-center justify-between gap-1 rounded-large border border-neutral-90 px-3 hover:border-neutral-60 has-[input:focus]:focus-input-outline`,
        className,
        {
          'h-10': size === 'large',
          'pr-1': hasValidChildren,
          'w-fit pl-5 pr-6': isFolded && !hasValue,
          'w-full': isFluid,
        },
      )}
    >
      <SearchIcon className="size-4 text-neutral-60" />
      <input
        required
        aria-describedby={t('common:search_aria')}
        role="searchbox"
        type="text"
        ref={mergeRefs(internalRef, ref)}
        placeholder={isFolded ? '' : placeholder}
        value={searchValue}
        onChange={handleChange}
        className={cn('absolute inset-0 box-border h-full w-full bg-transparent px-8 outline-none body-5', {
          'cursor-not-allowed': isDisabled,
          'pr-15': hasValue && hasValidChildren,
        })}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={isDisabled}
        onKeyDown={handleKeyDown}
      />
      <div className="relative flex items-center gap-1">
        {hasValue && isDefined(handleClear) && <ClearButton onClick={handleClear} />}
        {children}
      </div>
      {isFolded && !hasValue && <span className="body-6">{t('common:search')}</span>}
    </div>
  );
});
